import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
	Checkbox,
	FormControlLabel,
	Grid,
	TextField,
} from "@material-ui/core";
import {
	calculateUnitsUsed,
	parseReading,
	readingLabel,
	unitLabel,
} from "../utils/equipment-usage-math";

const useStyles = makeStyles((theme) => ({
	notes: {
		marginTop: theme.spacing(1),
	},
}));

const toInput = (value) => (value === null || value === undefined ? "" : value);

const EquipmentUsageFields = ({
	unitType,
	unitsUsed,
	startReading,
	endReading,
	notes,
	updateCurrentReading,
	onChange,
	disabled,
	showNotes = true,
}) => {
	const classes = useStyles();
	const unitsName = unitLabel(unitType);
	const endValue = parseReading(endReading);
	const canUpdateReading = endValue !== null;

	const patch = useCallback(
		(partial) => {
			const next = {
				unitsUsed,
				startReading,
				endReading,
				notes,
				updateCurrentReading,
				...partial,
			};
			const start = parseReading(next.startReading);
			const end = parseReading(next.endReading);
			if (
				partial.startReading !== undefined ||
				partial.endReading !== undefined
			) {
				const calculated = calculateUnitsUsed(start, end);
				if (calculated !== null && calculated >= 0) {
					next.unitsUsed = calculated;
				}
			}
			if (!parseReading(next.endReading)) {
				next.updateCurrentReading = false;
			}
			onChange(next);
		},
		[unitsUsed, startReading, endReading, notes, updateCurrentReading, onChange]
	);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} sm={4}>
				<TextField
					fullWidth
					variant="outlined"
					label={`Units used (${unitsName}) *`}
					type="number"
					inputProps={{ step: "any", min: "0" }}
					value={toInput(unitsUsed)}
					disabled={disabled}
					onChange={(e) => patch({ unitsUsed: parseReading(e.target.value) })}
				/>
			</Grid>
			<Grid item xs={12} sm={4}>
				<TextField
					fullWidth
					variant="outlined"
					label={readingLabel(unitType, "start")}
					type="number"
					inputProps={{ step: "any", min: "0" }}
					value={toInput(startReading)}
					disabled={disabled}
					onChange={(e) => patch({ startReading: parseReading(e.target.value) })}
				/>
			</Grid>
			<Grid item xs={12} sm={4}>
				<TextField
					fullWidth
					variant="outlined"
					label={readingLabel(unitType, "end")}
					type="number"
					inputProps={{ step: "any", min: "0" }}
					value={toInput(endReading)}
					disabled={disabled}
					onChange={(e) => patch({ endReading: parseReading(e.target.value) })}
				/>
			</Grid>
			{canUpdateReading && (
				<Grid item xs={12}>
					<FormControlLabel
						control={
							<Checkbox
								checked={!!updateCurrentReading}
								disabled={disabled}
								onChange={(e) =>
									patch({ updateCurrentReading: e.target.checked })
								}
								color="primary"
							/>
						}
						label="Update current reading on this equipment"
					/>
				</Grid>
			)}
			{showNotes && (
				<Grid item xs={12}>
					<TextField
						className={classes.notes}
						fullWidth
						variant="outlined"
						label="Notes"
						multiline
						rows={2}
						value={notes || ""}
						disabled={disabled}
						onChange={(e) => patch({ notes: e.target.value })}
					/>
				</Grid>
			)}
		</Grid>
	);
};

EquipmentUsageFields.propTypes = {
	unitType: PropTypes.string,
	unitsUsed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	startReading: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	endReading: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	notes: PropTypes.string,
	updateCurrentReading: PropTypes.bool,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	showNotes: PropTypes.bool,
};

export default EquipmentUsageFields;
